/** Math utility functions: scalar ops, 2D vector math, angle conversion, random helpers, and tile coordinate conversion. */
import type { Position } from './Types';
import { TILE_SIZE } from './Constants';

export const TAU = Math.PI * 2;
export const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

// --- Scalar utilities ---

/** Clamp a value to the [min, max] range. */
export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/** Linearly interpolate between a and b by factor t (0–1). */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Convert degrees to radians. */
export function degreesToRadians(deg: number): number {
  return deg * DEG_TO_RAD;
}

/** Convert radians to degrees. */
export function radiansToDegrees(rad: number): number {
  return rad * RAD_TO_DEG;
}

// --- Vector utilities ---

/** Return the magnitude (length) of a 2D vector. */
export function magnitude(x: number, y: number): number {
  return Math.sqrt(x * x + y * y);
}

/** Return the Euclidean distance between two points. */
export function distance(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Return the squared Euclidean distance between two points (avoids a sqrt). */
export function distanceSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return dx * dx + dy * dy;
}

/** Normalize a 2D vector to unit length; returns {0,0} for zero-length input. */
export function normalize(x: number, y: number): Position {
  const len = Math.sqrt(x * x + y * y);
  if (len === 0) return { x: 0, y: 0 };
  return { x: x / len, y: y / len };
}

/**
 * Normalize a direction vector and scale it to the given speed.
 * Returns {0, 0} if the input vector has zero length (avoids division by zero).
 */
export function scaledNormalize(dx: number, dy: number, speed: number): { x: number; y: number } {
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return { x: 0, y: 0 };
  return { x: (dx / len) * speed, y: (dy / len) * speed };
}

/** Return the dot product of two 2D vectors. */
export function dot(ax: number, ay: number, bx: number, by: number): number {
  return ax * bx + ay * by;
}

/** Rotate a 2D vector by a precomputed cos/sin pair. */
export function rotate2D(x: number, y: number, cos: number, sin: number): Position {
  return { x: x * cos - y * sin, y: x * sin + y * cos };
}

// --- Angle utilities ---

/** Convert an angle in radians to a unit direction vector. */
export function angleToDirection(radians: number): Position {
  return { x: Math.cos(radians), y: Math.sin(radians) };
}

/** Return the angle in radians for a direction vector via atan2. */
export function directionToAngle(x: number, y: number): number {
  return Math.atan2(y, x);
}

// --- Random utilities ---

/** Return a random float in [min, max). */
export function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Returns a random value in [-halfRange, +halfRange], centered on zero. */
export function randomBidirectional(halfRange: number): number {
  return (Math.random() - 0.5) * 2 * halfRange;
}

/** Return a random angle in [0, TAU). */
export function randomAngle(): number {
  return Math.random() * TAU;
}

/** Fisher-Yates shuffle an array in place and return it. */
export function shuffle<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

// --- Debug formatting ---

/** Format a world position as a rounded "(x,y)" string for debug output. */
export function formatPos(x: number, y: number): string {
  return `(${Math.round(x)},${Math.round(y)})`;
}

// --- Tile conversion ---

/** Convert a world pixel coordinate to a tile grid index. */
export function worldToTile(worldCoord: number): number {
  return Math.floor(worldCoord / TILE_SIZE);
}

/** Convert a tile grid index to the world pixel coordinate at the tile's center. */
export function tileToWorld(tileCoord: number): number {
  return tileCoord * TILE_SIZE + TILE_SIZE / 2;
}
